import * as React from 'react';
import { isNil } from 'lodash';

import MetricsBar from 'components/metrics-bar/MetricsBar';
import MetricLabelWithTooltip from './MetricLabelWithTooltip';

import { insertIf } from 'utils/list';
import { __t, applyTranslations } from 'utils/language';
import { getMetricValue, getRawMetricValue } from 'utils/helper/common';
import {
  getCostPerXValue,
  getFormattedAdsToContentRate,
  getFormattedAmount,
  getFormattedPercentageRate
} from 'utils/formatters';
import { getPriceRecommendationRange } from 'utils/helper/price';
import { isBrandsFocusedOrganization } from 'utils/organization';

import type { IndustryBenchmark, Metrics, Organization, StatisticsAudienceScore } from 'types';

import './channel-metrics.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./channel-metrics.i18n.json'));

type OwnProps = {
  hasRecentPosts: boolean,
  metrics: Metrics,
  benchmarksResource: IndustryBenchmark,
  isFreemium: boolean | null | undefined,
  audienceCredibility: StatisticsAudienceScore | undefined | null,
  audienceReachabilityScore: StatisticsAudienceScore | undefined | null,
  currentOrganization: Organization | undefined | null,
  minPostAgeInDays?: number,
  isSpecialTempInfluencer: boolean
};

type Props = OwnProps;

const DEFAULT_MIN_POST_AGE_IN_DAYS = 90;

const ChannelMetricsIG: React.FunctionComponent<Props> = (props: Props) => {
  const {
    metrics,
    audienceCredibility,
    audienceReachabilityScore,
    currentOrganization,
    benchmarksResource,
    isFreemium,
    hasRecentPosts,
    minPostAgeInDays,
    isSpecialTempInfluencer
  } = props;

  const median = benchmarksResource?.postFollowersEngagement?.median;

  if (Object.keys(metrics).length === 0) {
    return null;
  }

  const adsToContentRatio = getRawMetricValue(metrics, 'ads_ratio');
  const isBrand = isBrandsFocusedOrganization(currentOrganization);

  const priceRecoPost = getPriceRecommendationRange(
    getMetricValue(metrics, 'min_price_per_post'),
    getMetricValue(metrics, 'max_price_per_post')
  );

  const priceRecoStory = getPriceRecommendationRange(
    getMetricValue(metrics, 'min_price_per_story'),
    getMetricValue(metrics, 'max_price_per_story')
  );

  const genericMetrics = [
    // followers
    // COLLAB-14071 & COLLAB-17556
    ...insertIf(!isSpecialTempInfluencer, {
      key: 'channel-metric-followers-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.followers')}
          tooltip={__t('channel_metrics.followers_tooltip_instagram')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'followers') ?? 0)
    }),

    // Instagram data
    // Audience credibility
    ...insertIf(audienceCredibility, {
      key: 'channel-metric-audience-credibility-group-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.section_audience_credibility_title')}
          tooltip={__t('channel_metrics.title_audience_credibility_tooltip')}
        />
      ),
      value: __t(`collabary_common.score_label.${String(audienceCredibility)}`)
        || __t('collabary_common.score_label.unknown')
    }),

    // Reachability score
    ...insertIf(audienceReachabilityScore, {
      key: 'channel-metric-audience-reachability-score-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.section_audience_reachability_title')}
          tooltip={__t('channel_metrics.title_audience_reachability_tooltip')}
        />
      ),
      value: __t(`collabary_common.score_label.${String(audienceReachabilityScore)}`)
        || __t('collabary_common.score_label.unknown')
    }),

    ...insertIf(!isNil(adsToContentRatio), {
      key: 'channel-metric-audience-ads-to-content-ratio-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.ads_to_content_ratio')}
          tooltip={__t('channel_metrics.ads_to_content_ratio_tooltip')}
        />
      ),
      value: getFormattedAdsToContentRate(adsToContentRatio)
    })
  ];

  const engagementRate = getFormattedPercentageRate(getRawMetricValue(metrics, 'engagement_rate'));

  const igPostMetrics = [
    // engagement
    {
      key: 'channel-metric-engagement-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.engagement_instagram')}
          tooltip={__t('channel_metrics.engagement_instagram_tooltip')}
        />
      ),
      value: engagementRate,
      industryBenchmark: !isNil(median) ? `${median}%` : 'n/a',
      popover: hasRecentPosts && engagementRate === 'n/a' ? (
        <div className="channel-metric__info">
          {__t('channel_metrics.engagement_rate_not_available_info', { numOfDays: minPostAgeInDays || DEFAULT_MIN_POST_AGE_IN_DAYS })}
        </div>
      ) : null
    },
    // Reco price per post
    ...insertIf(isBrand && priceRecoPost,
      {
        key: 'channel-metric-price-instagram',
        name: (
          <MetricLabelWithTooltip
            label={__t('channel_metrics.price_post')}
            tooltip={__t('channel_metrics.price_post_tooltip')}
          />
        ),
        value: isFreemium ? 'xxx-xxx' : priceRecoPost
      },

      // Cost per thousand impressions
      {
        key: 'channel-metric-cpm-instagram',
        name: (
          <MetricLabelWithTooltip
            label={__t('channel_metrics.cpm')}
            tooltip={__t('channel_metrics.cpm_tooltip')}
          />
        ),
        value: isFreemium ? 'xxx' : getCostPerXValue(getRawMetricValue(metrics, 'cpm'))
      },

      // Cost per engagement
      {
        key: 'channel-metric-cpe-instagram',
        name: (
          <MetricLabelWithTooltip
            label={__t('channel_metrics.cpe')}
            tooltip={__t('channel_metrics.cpe_tooltip')}
          />
        ),
        value: isFreemium ? 'xxx' : getCostPerXValue(getRawMetricValue(metrics, 'cpe'))
      }
    ),

    // Post impressions
    {
      key: 'channel-metric-post-impressions-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.post_impressions')}
          tooltip={__t('channel_metrics.post_impressions_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'post_impressions'))
    },

    // Post reach
    {
      key: 'channel-metric-post-reach-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.post_reach')}
          tooltip={__t('channel_metrics.post_reach_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'post_reach'))
    },

    // Post likes comment ratio
    {
      key: 'channel-metric-post-comment-likes-ratio-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.post_comment_likes_ratio')}
          tooltip={__t('channel_metrics.post_comment_likes_ratio_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'post_comment_likes_ratio'))
    },

    // Posts per week
    {
      key: 'channel-metric-posts-per-week-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.posts_per_week')}
          tooltip={__t('channel_metrics.posts_per_week_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'posts_per_week'))
    }
  ];

  const igStoryMetrics = [
    // Cost per thousand impressions
    {
      key: 'channel-metric-story-impressions-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.story_impressions')}
          tooltip={__t('channel_metrics.story_impressions_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'story_impressions'))
    },

    // Cost per engagement
    {
      key: 'channel-metric-story-reach-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.story_reach')}
          tooltip={__t('channel_metrics.story_reach_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'story_reach'))
    },

    ...insertIf(isBrand && priceRecoStory, {
      key: 'channel-metric-price-story-instagram',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.price_story')}
          tooltip={__t('channel_metrics.price_story_tooltip')}
        />
      ),
      value: isFreemium ? 'xxx-xxx' : priceRecoStory
    })
  ];

  return (
    <React.Fragment>
      <section className="channel-metrics">
        <MetricsBar
          metrics={genericMetrics}
          metricsType={__t('channel_metrics.general_section_label')}
        />
      </section>
      <section className="channel-metrics">
        <MetricsBar
          metrics={igPostMetrics}
          metricsType={__t('channel_metrics.posts_section_label')}
        />
      </section>
      <section className="channel-metrics">
        <MetricsBar
          metrics={igStoryMetrics}
          metricsType={__t('channel_metrics.stories_section_label')}
        />
      </section>
    </React.Fragment>
  );
};

export default ChannelMetricsIG;
